import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MODAL_MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/components/modalLayout';
import { ModalLayoutContext } from '@cp/ds/src/components/modalLayout/context';
import { useMediaQuery } from '@mui/material';
import React, { ComponentType } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { useModalResolver } from '@cp/shared/modal';
import { useDispatch } from 'react-redux';
import { modalQueueSlice, useActiveModal, useQueueLength } from 'src/shared/modalProvider';
import { CommonModalProps } from '../modals/model';
import { ModalDictionary, ModalProviderProps } from './model';
import { modalsWithFullscreenMobileView } from './const';

const MODAL_TRANSITION_DURATION = 300;
const MODAL_QUEUE_MAX_LENGTH = 1;
const ssr = (m) => {
  return { then: (r) => r(m) };
};

const SwipeableDrawer = React.lazy(() =>
  // @ts-expect-error webpack require
  // eslint-disable-next-line @typescript-eslint/no-require-imports,no-undef
  typeof window === 'undefined' ? ssr(require('@mui/material/SwipeableDrawer')) : import('@mui/material/SwipeableDrawer'),
);
// @ts-expect-error webpack require
// eslint-disable-next-line @typescript-eslint/no-require-imports,no-undef
const Dialog = React.lazy(() => (typeof window === 'undefined' ? ssr(require('@mui/material/Dialog')) : import('@mui/material/Dialog')));

type ModalProps = CommonModalProps & { passedProps: ModalDictionary[keyof ModalDictionary] };

class EmptyComponent extends React.Component<ModalProps> {
  render() {
    return null;
  }
  static displayName = 'EmptyComponent';
}

export const ModalProvider: React.FC<ModalProviderProps> = () => {
  const resolver = useModalResolver();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down(MODAL_MOBILE_BREAKPOINT_THRESHOLD));
  const queueLength = useQueueLength();
  const activeModal = useActiveModal();
  const [isOpen, setOpen] = React.useState(Boolean(activeModal));
  const [ModalComponent, setModalComponent] = React.useState<ComponentType<ModalProps>>(() => EmptyComponent);

  const { onModalDestroy, closable = true, ...activeModalProps } = activeModal?.props ?? {};

  React.useEffect(() => {
    if (!activeModal) {
      return;
    }

    let isEffectCanceled = false;

    (async () => {
      const Component = await resolver.resolveComponent<ModalProps>(activeModal.name);
      if (!isEffectCanceled) {
        setModalComponent(() => Component);
        setOpen(true);
      }
    })();

    return () => {
      isEffectCanceled = true;
    };
  }, [activeModal, resolver]);

  const onClosingTransitionEnd = React.useCallback(() => {
    dispatch(modalQueueSlice.actions.shift());
    setModalComponent(() => EmptyComponent);
    onModalDestroy?.();
  }, [dispatch, onModalDestroy]);

  const onOpen = React.useCallback(() => setOpen(true), []);
  const onClose = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!queueLength || queueLength > MODAL_QUEUE_MAX_LENGTH) {
      setOpen(false);
    }
  }, [queueLength]);

  // @ts-expect-error new modal provider api in development
  const isFullscreen = isMobile && (activeModalProps.fullscreen || modalsWithFullscreenMobileView.includes(activeModal?.name));

  // ToDo: render backdrop with loader if currentModal && !activeModalComponentRef.current

  return (
    <ModalLayoutContext.Provider value={{ isFullscreen }}>
      {isMobile && !isFullscreen ? (
        <SwipeableDrawer
          PaperProps={{
            sx: {
              borderTopLeftRadius: `${theme.borderRadius.large}px`,
              borderTopRightRadius: `${theme.borderRadius.large}px`,
              overflow: 'visible',
              backfaceVisibility: 'hidden',
            },
          }}
          SlideProps={{ onExited: onClosingTransitionEnd }}
          anchor="bottom"
          onClose={closable ? onClose : () => undefined}
          onOpen={onOpen}
          onTouchStart={(event) => {
            /**
             * @link https://github.com/mui/material-ui/issues/18884#issuecomment-1523981868
             */
            (event.nativeEvent as any).defaultMuiPrevented = !closable;
          }}
          open={isOpen}
          sx={{ zIndex: 50 }}
          transitionDuration={MODAL_TRANSITION_DURATION}
          variant="temporary"
        >
          {/* @ts-expect-error new modal provider api in development */}
          <ModalComponent onClose={onClose} passedProps={activeModalProps} {...activeModalProps} />
        </SwipeableDrawer>
      ) : (
        <Dialog
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          PaperComponent={(isFullscreen ? Box : undefined) as any}
          PaperProps={
            isFullscreen
              ? {
                  sx: [
                    {
                      m: 0,
                      maxHeight: '100%',
                      maxWidth: '100% !important',
                      width: '100%',
                      height: '100%',
                      backgroundColor: theme.palette.white,
                    },
                    // { maxHeight: '100vh', maxWidth: '100vw', width: '100vw', height: '100vh' },
                  ],
                }
              : {
                  sx: { borderRadius: `${theme.borderRadius.extraLarge}px`, overflow: 'visible', maxWidth: 'none' },
                }
          }
          TransitionProps={{ onExited: onClosingTransitionEnd }}
          onClose={closable ? onClose : () => undefined}
          open={isOpen}
          scroll="body"
          sx={{ zIndex: 50 }}
          transitionDuration={MODAL_TRANSITION_DURATION}
        >
          {/* @ts-expect-error new modal provider api in development */}
          <ModalComponent onClose={onClose} passedProps={activeModalProps} {...activeModalProps} />
        </Dialog>
      )}
    </ModalLayoutContext.Provider>
  );
};
